import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';
import {
  conditionLabel, fileScriptRole, filterCategories, filterRecords, formatBytes, formatOccurs, formatRange,
  type FileScriptRole, type SpecCategoryDef, type SpecFieldDef, type SpecItemDef, type SpecRecordDef,
  type SpecScriptDef, type SpecSummary, type SpecTotDef, type SpecValue,
} from '../../lib/specs/specView';

/**
 * The EBTS specification browser.
 *
 * Until this page there was no way to see what a specification says. The rules decide whether a customer's
 * EFT is accepted, they are the thing a workflow is configured against, and the only view of them was the
 * workflow editor's field picker — a flat list of the fields you may map, which answers "what can I put on
 * the form" and nothing else. Every other question ("what does a DOCE consist of", "why is 2.041 rejecting
 * this", "what are the valid citizenship codes") meant reading a quarter-megabyte rule file by hand.
 *
 * Read-only. Editing is the next pass and it is a different problem: a stored specification with includes has
 * to be materialised to disk before it can be parsed, and the `.js` files run inside the validator. This page
 * is deliberately built first — a specification you can navigate is what tells you what an editor has to make
 * editable.
 */

export const specPath = (spec: string) => `/app/admin/specs/${encodeURIComponent(spec)}`;

const Restricted = () => (
  <div className="panel empty">
    <h3>Restricted</h3>
    <p>EBTS specifications are available to Lakota platform staff only.</p>
  </div>
);

export function Specifications() {
  const isAdmin = useIsSiteAdmin();
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['admin-specs'],
    queryFn: () => api.get<{ specifications: SpecSummary[] }>('/admin/specs'),
    enabled: isAdmin,
  });

  if (!isAdmin) return <Restricted />;

  const rows = list.data?.specifications ?? [];

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>EBTS specifications</h1>
          <div className="sub">
            The rulebooks a transaction is validated against — which records it carries, which fields are
            mandatory, and what may go in each one.
          </div>
        </div>
      </div>

      <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
        {list.isLoading ? <p className="loading-screen">// loading…</p> : rows.length === 0 ? (
          <p className="cart-note" style={{ textAlign: 'left', padding: 16 }}>// no specifications are bundled</p>
        ) : (
          <table className="tx-table">
            <thead><tr><th>Specification</th><th>Transaction types</th><th>Files</th><th></th></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="row-click" onClick={() => navigate(specPath(s.id))}>
                  <td className="mono">{s.id}</td>
                  <td>{s.tots.length}</td>
                  {/* A specification is a tree: EBTS 11.0 is a rule file, two script libraries and four lookup tables. */}
                  <td>{s.files.length}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Link className="track-link" to={specPath(s.id)} onClick={(e) => e.stopPropagation()}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="field-help" style={{ marginTop: 12 }}>
        These ship in the API bundle and are versioned with the code that interprets them. Changing one is a
        deploy, not a setting.
      </p>
    </div>
  );
}

export function SpecificationDetailPage() {
  const { spec = '' } = useParams();
  const isAdmin = useIsSiteAdmin();
  /*
   * The selected transaction type and tab live in the URL. A specification browser is a thing you send
   * someone a link to — "look at 2.041 on a DOCE" — and a component state cannot be linked to.
   */
  const [params, setParams] = useSearchParams();
  const tot = params.get('tot') ?? '';
  const raw = params.get('tab');
  const tab: 'transactions' | 'files' | 'scripts' = raw === 'files' || raw === 'scripts' ? raw : 'transactions';

  const cats = useQuery({
    queryKey: ['admin-spec-transactions', spec],
    queryFn: () => api.get<{ categories: SpecCategoryDef[] }>(`/admin/specs/${encodeURIComponent(spec)}/transactions`),
    enabled: isAdmin && !!spec,
  });
  const summary = useQuery({
    queryKey: ['admin-specs'],
    queryFn: () => api.get<{ specifications: SpecSummary[] }>('/admin/specs'),
    enabled: isAdmin,
  });
  /*
   * Fetched for the Files tab as well as its own, because it is the only thing that says which files are
   * code: `.js` is not the question. Cheap after the first — it is one query key for the whole page.
   */
  const scripts = useQuery({
    queryKey: ['admin-spec-scripts', spec],
    queryFn: () => api.get<{ scripts: SpecScriptDef[] }>(`/admin/specs/${encodeURIComponent(spec)}/scripts`),
    enabled: isAdmin && !!spec,
  });

  if (!isAdmin) return <Restricted />;

  const categories = cats.data?.categories ?? [];
  const found = summary.data?.specifications.find((s) => s.id === spec);
  const files = found?.files ?? [];
  const selected = categories.flatMap((c) => c.transactions).find((t) => t.tot === tot);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <div className="console-head">
        <div>
          <h1 className="mono">{spec}</h1>
          <div className="sub">
            <Link to="/app/admin/specs" className="track-link">← EBTS specifications</Link>
            {cats.data && <>{' · '}{categories.reduce((n, c) => n + c.transactions.length, 0)} transaction types</>}
          </div>
        </div>
        <div className="seg">
          <button type="button" className={`seg-btn ${tab === 'transactions' ? 'is-active' : ''}`} onClick={() => setParam('tab', '')}>
            Transactions
          </button>
          <button type="button" className={`seg-btn ${tab === 'files' ? 'is-active' : ''}`} onClick={() => setParam('tab', 'files')}>
            Files ({files.length})
          </button>
          <button type="button" className={`seg-btn ${tab === 'scripts' ? 'is-active' : ''}`} onClick={() => setParam('tab', 'scripts')}>
            Scripts ({scripts.data?.scripts.length ?? 0})
          </button>
        </div>
      </div>

      {tab === 'scripts'
        ? <ScriptsTab scripts={scripts.data?.scripts ?? []} loading={scripts.isLoading} ruleFile={found?.ruleFile} />
        : tab === 'files'
        ? (
          <FilesTab
            spec={spec} files={files} ruleFile={found?.ruleFile}
            scripts={scripts.data?.scripts ?? []} loading={summary.isLoading || scripts.isLoading}
          />
        )
        : cats.isLoading ? <p className="loading-screen">// reading the specification…</p>
        : cats.isError ? <div className="panel empty"><h3>Could not read {spec}</h3></div>
        : (
          <div className="spec-layout">
            <TotPicker categories={categories} selected={tot} onSelect={(t) => setParam('tot', t)} />
            {selected
              ? <TransactionView spec={spec} tot={selected} />
              : (
                <div className="panel empty">
                  <h3>Pick a transaction type</h3>
                  <p>
                    A specification repeats every field for each transaction type, because that is what decides
                    whether the field is mandatory. There is no view of "the fields" without one.
                  </p>
                </div>
              )}
          </div>
        )}
    </div>
  );
}

/** The left-hand list: the specification's own categories, its own descriptions, filtered as one. */
function TotPicker({ categories, selected, onSelect }: {
  categories: SpecCategoryDef[]; selected: string; onSelect: (tot: string) => void;
}) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => filterCategories(categories, q), [categories, q]);

  return (
    <div className="panel spec-picker">
      <input
        className="spec-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Filter by code or name…" aria-label="Filter transaction types"
      />
      {shown.length === 0 && <p className="field-help" style={{ padding: '8px 2px' }}>// nothing matches “{q}”</p>}
      {shown.map((c) => (
        <div key={c.name} className="spec-cat">
          <div className="spec-cat-name">{c.name}</div>
          {c.transactions.map((t) => (
            <button
              key={t.tot} type="button"
              className={`spec-tot ${t.tot === selected ? 'is-active' : ''}`}
              onClick={() => onSelect(t.tot)}
            >
              <span className="mono spec-tot-code">{t.tot}</span>
              {t.description && <span className="spec-tot-desc">{t.description}</span>}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/** One transaction type: what it must carry, then its records, fields and items. */
function TransactionView({ spec, tot }: { spec: string; tot: SpecTotDef }) {
  const [q, setQ] = useState('');
  const tree = useQuery({
    queryKey: ['admin-spec-tree', spec, tot.tot],
    queryFn: () => api.get<{ records: SpecRecordDef[] }>(
      `/admin/specs/${encodeURIComponent(spec)}/tree?tot=${encodeURIComponent(tot.tot)}`,
    ),
  });

  const records = tree.data?.records ?? [];
  const shown = useMemo(() => filterRecords(records, q), [records, q]);
  const total = records.reduce((n, r) => n + r.fields.length, 0);

  return (
    <div className="spec-body">
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>
          <span className="mono">{tot.tot}</span>
          {tot.description && <span className="spec-tot-title"> — {tot.description}</span>}
        </h3>
        {/*
          * The record requirements come from the TRANSACTIONS section, not from the field rules: they are
          * what a transaction type IS before you look at any field — the records it carries and how many.
          */}
        <div className="spec-reqs">
          {tot.records.map((r) => {
            const occurs = formatOccurs(r.min, r.max);
            return (
              <span key={r.recordType} className={`pill ${r.min > 0 ? 'pill-scan' : ''}`}>
                Type-{r.recordType}{occurs && <span className="spec-req-occurs"> {occurs}</span>}
              </span>
            );
          })}
        </div>
      </div>

      <div className="spec-toolbar">
        <input
          className="spec-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter fields — 2.041, T2_RES, residence…" aria-label="Filter fields"
        />
        <span className="field-help">
          {tree.isLoading ? 'loading…' : q.trim()
            ? `${shown.reduce((n, r) => n + r.fields.length, 0)} of ${total} fields`
            : `${total} fields`}
        </span>
      </div>

      {tree.isLoading ? <p className="loading-screen">// reading {tot.tot}…</p>
        : shown.length === 0 ? (
          <div className="panel empty">
            <h3>{q.trim() ? 'No field matches' : 'No fields'}</h3>
            {q.trim() && <p>Nothing in {tot.tot} matches “{q}”.</p>}
          </div>
        ) : shown.map((r) => (
          <RecordSection
            key={r.recordType} record={r}
            requirement={tot.records.find((x) => x.recordType === r.recordType)}
            /* Filtering is the act of looking for something; leaving the results collapsed hides the answer. */
            openByDefault={!!q.trim() || r.recordType <= 2}
          />
        ))}
    </div>
  );
}

function RecordSection({ record, requirement, openByDefault }: {
  record: SpecRecordDef;
  requirement: { min: number; max: number } | undefined;
  openByDefault: boolean;
}) {
  const occurs = requirement ? formatOccurs(requirement.min, requirement.max) : '';
  return (
    <details className="panel spec-record" open={openByDefault}>
      <summary className="spec-record-head">
        <span className="spec-record-name">Type-{record.recordType}</span>
        <span className="field-help">
          {record.fields.length} field{record.fields.length === 1 ? '' : 's'}
          {occurs && ` · ${occurs}`}
        </span>
      </summary>
      <div className="spec-fields">
        {record.fields.map((f) => <FieldRow key={f.ref} field={f} />)}
      </div>
    </details>
  );
}

function FieldRow({ field }: { field: SpecFieldDef }) {
  const occurs = formatOccurs(field.occursMin, field.occursMax);
  const hasDetail = !!field.longDescription || !!field.values?.length || field.items.length > 0;

  return (
    <details className="spec-field">
      <summary className="spec-field-head">
        <span className="mono spec-ref">{field.ref}</span>
        <span className="mono spec-mnemonic">{field.mnemonic}</span>
        {field.condition && (
          <span className={`pill ${field.condition === 'M' ? 'pill-scan' : ''}`}>{conditionLabel(field.condition)}</span>
        )}
        <span className="spec-field-desc">{field.description}</span>
        {!hasDetail && <span className="spec-field-flat" aria-hidden="true" />}
      </summary>

      <div className="spec-field-body">
        <Facts field={field} occurs={occurs} />
        {field.longDescription && <p className="spec-long">{field.longDescription}</p>}
        {field.values?.length ? <ValueList values={field.values} mandatory={field.valuesMandatory} /> : null}
        {field.items.length > 0 && (
          <div className="spec-items">
            <div className="spec-items-head">
              {field.items.length} item{field.items.length === 1 ? '' : 's'} within this field
            </div>
            {field.items.map((i) => <ItemRow key={i.ref} item={i} />)}
          </div>
        )}
      </div>
    </details>
  );
}

/** The shape rules, as a row of labelled facts rather than a sentence — they are looked up, not read. */
function Facts({ field, occurs }: { field: SpecFieldDef | SpecItemDef; occurs?: string }) {
  const facts: [string, string][] = [
    ['Type', field.characterType],
    ['Length', formatRange(field.minLength, field.maxLength)],
  ];
  if (field.characterSet) facts.push(['Character set', field.characterSet]);
  if (occurs) facts.push(['Occurs', occurs]);
  if (field.defaultValue) facts.push(['Default', field.defaultValue]);
  if ('example' in field && field.example) facts.push(['Example', field.example]);

  return (
    <div className="spec-facts">
      {facts.map(([k, v]) => (
        <span key={k} className="spec-fact">
          <span className="spec-fact-k">{k}</span>
          <span className="mono spec-fact-v">{v}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * A field's permitted values.
 *
 * Collapsed and filterable because the long ones are very long: T2_SMT carries 851 scar/mark/tattoo codes,
 * place of birth 640, citizenship 241. Rendering those inline would bury every other field on the record,
 * and they are the codes you would open this page to look up — so they are here, just not in the way.
 */
function ValueList({ values, mandatory }: { values: SpecValue[]; mandatory?: boolean | undefined }) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? values.filter((v) => v.code.toLowerCase().includes(needle) || v.label.toLowerCase().includes(needle))
    : values;

  return (
    <details className="spec-values">
      <summary>
        {values.length} permitted value{values.length === 1 ? '' : 's'}
        <span className="field-help"> · {mandatory ? 'the value must be one of these' : 'suggested, not enforced'}</span>
      </summary>
      {values.length > 12 && (
        <input
          className="spec-search spec-search-sm" type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter values…" aria-label="Filter permitted values"
        />
      )}
      {shown.length === 0 ? <p className="field-help">// nothing matches “{q}”</p> : (
        <ul className="spec-value-list">
          {shown.map((v) => (
            <li key={v.code}>
              <span className="mono spec-value-code">{v.code}</span>
              <span className="spec-value-label">{v.label}</span>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

function ItemRow({ item }: { item: SpecItemDef }) {
  return (
    <details className="spec-item">
      <summary className="spec-field-head">
        <span className="mono spec-ref">{item.ref}</span>
        <span className="mono spec-mnemonic">{item.mnemonic}</span>
        {item.condition && (
          <span className={`pill ${item.condition === 'M' ? 'pill-scan' : ''}`}>{conditionLabel(item.condition)}</span>
        )}
        <span className="spec-field-desc">{item.description}</span>
      </summary>
      <div className="spec-field-body">
        <Facts field={item} />
        {item.values?.length ? <ValueList values={item.values} mandatory={item.valuesMandatory} /> : null}
      </div>
    </details>
  );
}

/** What each role means to someone deciding whether a file is safe to change. */
const ROLE_NOTE: Record<FileScriptRole, string | null> = {
  'loaded-script': 'Executes during verification. This specification loads it.',
  'inline-script': 'Carries a SCRIPT_BEGIN block — this rule file also executes, despite its name.',
  'orphan-script': 'This specification never loads it. Editing it would change nothing.',
  data: null,
};

const ROLE_TAG: Record<FileScriptRole, string | null> = {
  'loaded-script': 'script',
  'inline-script': 'inline script',
  'orphan-script': 'not loaded',
  data: null,
};

/**
 * The files the specification is made of.
 *
 * Worth seeing on its own: a specification is a tree, not a document — EBTS 11.0 is a rule file, two script
 * libraries and four lookup tables with twelve includes between them, and that is not visible from anywhere
 * else. This is a directory listing, though, and a directory listing does not say what runs — so each file is
 * labelled against what the specification reports LOADING, and two of the ATF files come back "not loaded".
 */
function FilesTab({ spec, files, ruleFile, scripts, loading }: {
  spec: string; files: string[]; ruleFile: string | undefined; scripts: SpecScriptDef[]; loading: boolean;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const content = useQuery({
    queryKey: ['admin-spec-file', spec, open],
    queryFn: () => api.get<{ content: string }>(
      `/admin/specs/${encodeURIComponent(spec)}/file?path=${encodeURIComponent(open ?? '')}`,
    ),
    enabled: !!open,
  });

  if (loading) return <p className="loading-screen">// loading…</p>;

  const openRole = open ? fileScriptRole(open, scripts, ruleFile) : null;

  return (
    <div className="spec-layout">
      <div className="panel spec-picker">
        {files.map((f) => {
          const role = fileScriptRole(f, scripts, ruleFile);
          return (
            <button
              key={f} type="button" className={`spec-tot ${f === open ? 'is-active' : ''}`}
              onClick={() => setOpen(f)}
            >
              <span className="mono spec-tot-code">{f}</span>
              {ROLE_TAG[role] && <span className={`spec-role spec-role-${role}`}>{ROLE_TAG[role]}</span>}
            </button>
          );
        })}
      </div>

      <div className="spec-body">
        {!open ? (
          <div className="panel empty">
            <h3>Pick a file</h3>
            <p>The rule file, its script includes and its lookup tables, as they ship in the API bundle.</p>
          </div>
        ) : content.isLoading ? <p className="loading-screen">// reading {open}…</p>
          : content.isError ? <div className="panel empty"><h3>Could not read {open}</h3></div>
          : (
            <div className="panel" style={{ overflow: 'hidden' }}>
              <div className="spec-file-head">
                <span className="mono">{open}</span>
                <span className="field-help">{openRole ? ROLE_NOTE[openRole] ?? 'Rules and lookup data.' : ''}</span>
              </div>
              <pre className="spec-file">{content.data?.content}</pre>
            </div>
          )}
      </div>
    </div>
  );
}

/**
 * What the specification EXECUTES.
 *
 * This is the answer the file list cannot give. A specification's script may be an included `.js` or a
 * `SCRIPT_BEGIN` block in the rule file, and going by extension gets the ATF specification wrong in both
 * directions at once: it includes nothing, inlines 94KB, and keeps two `.js` files beside it that never run.
 *
 * It is also the trust boundary for the editor that follows. A stored specification whose script is editable
 * is a stored configuration that runs code inside the validator — which is a different kind of change from
 * altering a field's maximum length, and needs to look like one before anyone can make it.
 */
function ScriptsTab({ scripts, loading, ruleFile }: {
  scripts: SpecScriptDef[]; loading: boolean; ruleFile: string | undefined;
}) {
  if (loading) return <p className="loading-screen">// loading…</p>;

  if (scripts.length === 0) {
    return (
      <div className="panel empty">
        <h3>This specification runs no script</h3>
        <p>Every rule in it is expressed in tags alone. Nothing here executes during verification.</p>
      </div>
    );
  }

  return (
    <div className="spec-body">
      <p className="field-help" style={{ maxWidth: '76ch' }}>
        These run inside the validator when a transaction is verified, and a rule's <code>js_verify</code> tag
        calls into them.
      </p>
      {scripts.map((s, i) => (
        <details key={s.name ?? `inline-${i}`} className="panel spec-record">
          <summary className="spec-record-head">
            <span className="spec-record-name mono">{s.name ?? `inline in ${ruleFile ?? 'the rule file'}`}</span>
            <span className="field-help">{s.type} · {formatBytes(s.bytes)}</span>
            {!s.name && <span className="spec-role spec-role-inline-script">inline</span>}
          </summary>
          <div style={{ padding: '12px 16px' }}>
            <pre className="spec-file">{s.content}</pre>
          </div>
        </details>
      ))}
    </div>
  );
}
